// App — wouter router. Keeps splash on `/` and mounts all interviewee/admin
// pages under their respective path prefixes. Admin routes are lazy-loaded
// so respondents (99% of traffic) don't pay for the admin bundle.

import { lazy, Suspense } from 'react'
import { Route, Switch } from 'wouter'
import InviteLanding from './routes/InviteLanding'
import InviteForm from './routes/InviteForm'
import InviteReview from './routes/InviteReview'
import InviteSubmit from './routes/InviteSubmit'
import InviteDone from './routes/InviteDone'
import PublicStart from './routes/PublicStart'
import { Toaster } from '@/components/ui/toaster'
import { getStoredLang } from '@/lib/api'
import { t as tr } from '@/lib/i18n'

const AdminLogin = lazy(() => import('./routes/AdminLogin'))
const AdminIndex = lazy(() => import('./routes/AdminIndex'))
const AdminPack = lazy(() => import('./routes/AdminPack'))
const AdminInvitation = lazy(() => import('./routes/AdminInvitation'))
const AdminExport = lazy(() => import('./routes/AdminExport'))
const AdminTriage = lazy(() => import('./routes/AdminTriage'))
const AdminSignals = lazy(() => import('./routes/AdminSignals'))

function Splash() {
  return (
    <main className="min-h-screen bg-cream text-foreground flex items-center justify-center px-6">
      <div className="text-center max-w-2xl">
        <h1 className="font-serif text-5xl sm:text-6xl font-semibold tracking-tight leading-tight">
          Cows,{' '}
          <span className="relative inline-block">
            <span>Men</span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-1/2 h-[3px] bg-vermillion -rotate-3 -translate-y-1/2"
            />
          </span>
          , Paddocks
          <span className="block text-vermillion mt-2">— Interviews</span>
        </h1>
        <p className="font-serif italic text-xl sm:text-2xl mt-6 text-foreground/70">
          surviving the AI shift
        </p>
        <p className="mt-10 text-sm text-muted-foreground">
          You'll arrive here via a personal link. If you came looking for the
          admin surface, it lives at{' '}
          <a
            className="underline underline-offset-4 hover:text-foreground"
            href="/admin"
          >
            /admin
          </a>
          .
        </p>
      </div>
    </main>
  )
}

function NotFound() {
  const lang = getStoredLang()
  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <div className="max-w-md text-center space-y-2">
        <h1 className="font-serif text-3xl">{tr(lang, 'not_found_title')}</h1>
        <p className="text-muted-foreground">{tr(lang, 'not_found_body')}</p>
      </div>
    </main>
  )
}

function AdminFallback() {
  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <p className="text-sm text-muted-foreground">Loading admin…</p>
    </main>
  )
}

export default function App() {
  return (
    <>
      <Switch>
        <Route path="/" component={Splash} />

        <Route path="/p/:slug" component={PublicStart} />

        <Route path="/i/:token" component={InviteLanding} />
        <Route
          path="/i/:token/q/:sectionKey/:qId"
          component={InviteForm}
        />
        <Route path="/i/:token/review" component={InviteReview} />
        <Route path="/i/:token/submit" component={InviteSubmit} />
        <Route path="/i/:token/done" component={InviteDone} />

        <Route path="/admin/login">
          <Suspense fallback={<AdminFallback />}>
            <AdminLogin />
          </Suspense>
        </Route>
        <Route path="/admin">
          <Suspense fallback={<AdminFallback />}>
            <AdminIndex />
          </Suspense>
        </Route>
        <Route path="/admin/packs/:slug">
          <Suspense fallback={<AdminFallback />}>
            <AdminPack />
          </Suspense>
        </Route>
        <Route path="/admin/invitations/:id">
          <Suspense fallback={<AdminFallback />}>
            <AdminInvitation />
          </Suspense>
        </Route>
        <Route path="/admin/export">
          <Suspense fallback={<AdminFallback />}>
            <AdminExport />
          </Suspense>
        </Route>
        <Route path="/admin/triage">
          <Suspense fallback={<AdminFallback />}>
            <AdminTriage />
          </Suspense>
        </Route>
        <Route path="/admin/signals">
          <Suspense fallback={<AdminFallback />}>
            <AdminSignals />
          </Suspense>
        </Route>

        <Route component={NotFound} />
      </Switch>
      <Toaster />
    </>
  )
}
